import { useState } from "react"
import QrPreviewModal from "./QrPreviewModal"
import CodeActions from "./CodeActions"
import Sparkline from "./dashboard/Sparkline"

// One row in YourCodesCard. The thumbnail opens the QR preview; CodeActions
// holds copy / edit / analytics (inline on >= sm, a kebab menu on mobile so a
// narrow row doesn't overflow). Editing is an explicit action now — the whole
// row is no longer a click target. A locked code (per-code paywall crossed for
// a free user) shows a lock glyph next to its label; editing still opens the
// modal, which renders the upgrade lock-wall.
//
// In selection mode (`selectable`) a checkbox leads the row; the rest of the
// row keeps working. A grouped code shows its group as a quiet chip.
// `highlighted`: the code the builder's Done just handed to the list (#316).
//
// The list around the rows doesn't clip them (the row menu has to open past
// its edge, #334), so the first and last rows take its rounded corners for
// their own hover and highlight.
export default function CodeRow({ code, onEdit, highlighted = false, selectable = false, selected = false, onToggleSelect }) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const locked = code.editable === false
  const shortDisplay = (code.short_url || `tgo.sh/${code.token}`).replace(/^https?:\/\//, "")

  return (
    <div data-token={code.token} className="border-b border-[color:var(--color-border)] dark:border-storm last:border-b-0 first:rounded-t-[inherit] last:rounded-b-[inherit] hover:bg-[color:var(--color-cloud)]/50 dark:hover:bg-coal/50 transition">
      <div className={"px-4 py-3 flex gap-3 items-center rounded-[inherit] transition " + (selected || highlighted ? "bg-voltage-pale/60 dark:bg-voltage/10 " : "") + (highlighted ? "ring-2 ring-inset ring-voltage" : "")}>
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect?.(code)}
            aria-label={`Select ${code.display_label}`}
            className="w-4 h-4 flex-shrink-0 accent-[color:var(--color-voltage)] cursor-pointer"
          />
        )}
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          aria-label={`View QR code for ${shortDisplay}`}
          title="View QR code"
          className="w-[42px] h-[42px] border border-[color:var(--color-border)] dark:border-storm rounded-[7px] flex-shrink-0 p-0 cursor-pointer overflow-hidden hover:border-voltage transition"
          style={{ background: code.background_color || "#FFFFFF" }}
        >
          <img src={code.image_url} alt="" aria-hidden="true" className="w-full h-full" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-[15px] text-ink dark:text-cloud truncate" title={code.display_label}>
              {code.display_label}
            </span>
            {code.group && (
              <span
                className="flex-shrink-0 max-w-[40%] truncate rounded-full border border-[color:var(--color-border)] dark:border-storm px-1.5 py-px text-[11px] text-[color:var(--color-muted)] dark:text-muted-light"
                title={`Group: ${code.group.name}`}
              >
                {code.group.name}
              </span>
            )}
            {code.under_review && (
              <a
                href={code.appeal_url}
                className="flex-shrink-0 rounded-full border border-[color:var(--color-warn)] px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-warn)] hover:bg-[color:var(--color-warn)]/10 transition"
                title="This destination is on Google's unsafe list, so scans see a review page. Ask for a re-check."
              >
                Under review
              </a>
            )}
            {locked && (
              <span className="text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0" title="Locked · upgrade to edit" aria-label="Editing locked">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
            )}
          </div>
          <div className="font-mono text-[13px] text-[color:var(--color-muted)] dark:text-muted-light truncate mt-0.5">
            <span className="text-voltage-dark dark:text-voltage-light">{shortDisplay}</span>
            <span className="mx-1">→</span>
            <span title={code.destination_url}>{code.destination_url}</span>
          </div>
        </div>

        <div className="hidden min-[720px]:block flex-shrink-0">
          <Sparkline data={code.daily7 || []} />
        </div>

        <div className="text-right flex-shrink-0 w-12 sm:w-14">
          <div className="font-mono text-[15px] text-ink dark:text-cloud leading-none">{(code.scan_count || 0).toLocaleString()}</div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light mt-1">Scans</div>
        </div>

        <CodeActions code={code} onEdit={onEdit} />
      </div>

      <QrPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} code={code} />
    </div>
  )
}
